import { useEffect, useState } from "react";
import { fetchSettings, updateSettings } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Save } from "lucide-react";
import { toast } from "sonner";

const partyFields = [
  { key: "name", label: "Nombre / Razón social" },
  { key: "tax_id", label: "NIF / Tax ID" },
  { key: "address_line1", label: "Dirección" },
  { key: "address_line2", label: "Dirección (2ª línea)" },
  { key: "postal_code", label: "Código postal" },
  { key: "city", label: "Ciudad" },
  { key: "country", label: "País" },
  { key: "phone", label: "Teléfono" },
  { key: "email", label: "Email" },
  { key: "contact_name", label: "Persona de contacto" },
];

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings().then(setSettings).catch(() => toast.error("Error cargando ajustes"));
  }, []);

  const updateParty = (which, field, value) => {
    setSettings({ ...settings, [which]: { ...settings[which], [field]: value } });
  };

  const updateRoot = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const save = async () => {
    setSaving(true);
    try {
      const out = await updateSettings(settings);
      setSettings(out);
      toast.success("Ajustes guardados");
    } catch (e) {
      toast.error("Error guardando ajustes");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="text-slate-400">Cargando...</div>;

  return (
    <div data-testid="settings-page" className="space-y-8">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Configuración</div>
        <h1 className="font-cabinet text-4xl font-bold tracking-tighter text-slate-900 mt-1">Ajustes</h1>
        <p className="text-slate-500 mt-2">Datos fiscales del emisor y del cliente, y serie de numeración.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <PartyCard
          title="Emisor"
          subtitle="Datos de Viaja de Verdad"
          party={settings.issuer}
          onChange={(f, v) => updateParty("issuer", f, v)}
          testidPrefix="issuer"
        />
        <PartyCard
          title="Cliente"
          subtitle="Datos de Espíritu Travel"
          party={settings.recipient}
          onChange={(f, v) => updateParty("recipient", f, v)}
          testidPrefix="recipient"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h2 className="font-cabinet text-lg font-semibold text-slate-900 mb-4">Serie de numeración</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs font-medium text-slate-600">Prefijo</Label>
            <Input
              data-testid="series-prefix-input"
              value={settings.series_prefix}
              onChange={(e) => updateRoot("series_prefix", e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-600">Año vigente</Label>
            <Input
              type="number"
              data-testid="year-input"
              value={settings.year}
              onChange={(e) => updateRoot("year", Number(e.target.value))}
              className="mt-1.5"
            />
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-3">Formato: <span className="font-mono">{settings.series_prefix}/{String(settings.year).slice(-2)}/NN</span></p>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="save-settings-btn">
          <Save size={14} className="mr-2" /> Guardar ajustes
        </Button>
      </div>
    </div>
  );
}

function PartyCard({ title, subtitle, party, onChange, testidPrefix }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6">
      <h2 className="font-cabinet text-lg font-semibold text-slate-900">{title}</h2>
      <p className="text-xs text-slate-500 mt-1 mb-4">{subtitle}</p>
      <div className="grid gap-3">
        {partyFields.map((f) => (
          <div key={f.key}>
            <Label className="text-xs font-medium text-slate-600">{f.label}</Label>
            <Input
              data-testid={`${testidPrefix}-${f.key}-input`}
              value={party[f.key] || ""}
              onChange={(e) => onChange(f.key, e.target.value)}
              className="mt-1.5"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
